/**
 * Style groups (JF 2026-10-13, colleagues' feedback): one customer's styles side by side — fabrics and trims in order,
 * CM / other costs on the same row (a dash where a style has none), edited here directly
 * (each change lands on that style's own cost sheet), several styles set at once, and one Excel for the customer's first
 * approval. Without a group open: the list of groups and "new group" from saved cost sheets.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  cmPartsOf, coefOf, fromCny, fxOf, isSet, n, RANGE_SECTIONS, rangeColumns, rangeQtys, rangeRows, rangeTotals, rowIsItem, setCmParts, setFx, setRangeCell, setTierCell, switchTier, tierSheet,
  type RangeCell, type RangeRow, type RangeView, type Sheet,
} from "@ic/engine";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { api, type GroupInfo, type SheetSummary } from "../../shared/api.ts";
import { okToReplace } from "../../shared/unsaved.ts";
import { moneyFor, unitLabel } from "../../shared/money.ts";
import { Field, Modal } from "../../shared/ui/common.tsx";
import { localTime } from "../../shared/time.ts";
import { useGroup } from "./groupStore.ts";
import { groupSavedOrStay } from "./nav.ts";

export function GroupView() {
  const g = useGroup((s) => s.group);
  return g ? <GroupSheets /> : <GroupHome />;
}

/** One column: a style at the quantity picked at the top (or one quantity of a tiered quote when "all" is picked). */
interface Col { i: number; qty: number; sheet: Sheet; editable: boolean; first: boolean; span: number; missing: boolean }
/** one style's changes read back from Excel */
interface ImportStyle { i: number; label: string; conflict: boolean; changes: import("./importRange.ts").RangeChange[] }
const viewKey = (id: string) => "ic_grp_view_" + id;

function GroupSheets() {
  const t = useT();
  const lang = useStore((s) => s.lang);
  const cur = useStore((s) => s.cur);
  const dfx = useStore((s) => s.defaultFx);
  const { toast, go, setSheet, markSaved } = useStore.getState();
  const st = useGroup();
  const { group, sheets, mine } = st;
  // which quantity the styles are shown at (JF 2026-10-13): each sheet's own, one quantity for all, or every quantity
  const [view, setViewState] = useState<RangeView>(() => {
    try { const v = localStorage.getItem(viewKey(group?.id || "")); return v === "all" ? "all" : v && Number(v) > 0 ? Number(v) : "active"; } catch { return "active"; }
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  /** styles the bulk settings apply to; empty = every style */
  const [only, setOnly] = useState<Set<number>>(new Set());
  const [bulk, setBulk] = useState({ qty: "", fx: "", cmMult: "", docs: "", target: "" });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [onlyShown, setOnlyShown] = useState(false);
  const [imp, setImp] = useState<{ file: string; cur: "USD" | "CNY"; styles: ImportStyle[]; use: Set<number> } | null>(null);
  const impInput = useRef<HTMLInputElement>(null);
  const dirty = st.dirtyCount();
  const anyTiers = sheets.some((x) => x.data.tiers?.qtys.length);
  const qtys = useMemo(() => rangeQtys(sheets.map((x) => x.data)), [sheets]);

  /** style order (JF 2026-10-16): by style no. — numbers by value — or one style left / right */
  const styleNoOf = (i: number) => String(sheets[i].data.h.style || sheets[i].styleNo || "");
  const reorder = (order: number[]) => useGroup.getState().reorder(order).catch((e) => useStore.getState().toast(String(e.message || e)));
  const sortByNo = () => reorder(sheets.map((_, i) => i).sort((a, b) => styleNoOf(a).localeCompare(styleNoOf(b), undefined, { numeric: true, sensitivity: "base" })));
  const move = (i: number, d: -1 | 1) => { const o = sheets.map((_, k) => k); [o[i], o[i + d]] = [o[i + d], o[i]]; reorder(o); };
  const cols = useMemo<Col[]>(() => rangeColumns(sheets.map((x) => x.data), view).map((c) => {
    const s = sheets[c.s].data;
    // editable: someone's own style, at the quantity its cost sheet shows (a quantity picked at the top becomes that)
    const editable = sheets[c.s].mine && !c.missing && (!s.tiers?.qtys.length || s.tiers.active === c.qty);
    return { i: c.s, qty: c.qty, sheet: c.sheet, editable, first: c.first, span: c.span, missing: c.missing };
  }), [sheets, view]);
  /** one quantity for every style: their cost sheets switch to it (saved with "Save all"), so it can be edited here */
  function setView(v: RangeView) {
    setViewState(v);
    try { localStorage.setItem(viewKey(group!.id), String(v)); } catch { /* private mode */ }
    if (typeof v !== "number") return;
    sheets.forEach((x, i) => { if (x.mine && x.data.tiers?.qtys.includes(v) && x.data.tiers.active !== v) st.edit(i, (s) => switchTier(s, v)); });
  }
  const rows = useMemo(() => rangeRows(cols.map((c) => c.sheet)), [cols]);
  const tot = useMemo(() => rangeTotals(cols.map((c) => c.sheet), rows), [cols, rows]);
  if (!group) return null;
  // every style at its own rate; the switch at the top picks the currency
  const money = (c: Col) => moneyFor(cur, fxOf(c.sheet, dfx));
  const styleName = (i: number) => sheets[i].data.h.style || sheets[i].styleNo || String(i + 1);

  function back() {
    if (dirty && !confirm(t(`${dirty} style(s) have unsaved changes. Leave without saving?`, `有 ${dirty} 款的修改还没保存，确定不保存就离开？`))) return;
    st.close();
  }
  async function save() {
    setSaving(true);
    try { const k = await st.saveAll(); toast(t(`Saved ${k} style(s)`, `已保存 ${k} 款`)); }
    catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setSaving(false); }
  }
  function setCell(c: Col, cell: RangeCell, f: "px" | "yld" | "per", v: string) {
    st.edit(c.i, (s) => {
      if (f === "px") { const mv = money(c).typed(v); if (!s.fx && cur === "CNY") s.fx = fxOf(s, dfx); setRangeCell(s, cell.ref, "px", mv); }
      else setRangeCell(s, cell.ref, f, v);
    });
  }
  /** a CM / other-cost row: the same amount on every style (or the ones picked in the bulk panel), in the currency shown */
  function rowToAll(r: RangeRow) {
    const v = prompt(t(`"${r.en}": the same amount for ${only.size ? "the picked" : "every"} style (${cur === "CNY" ? "RMB" : "USD"}):`, `「${r.zh}」：${only.size ? "选中的" : "所有"}款统一为（${cur === "CNY" ? "人民币" : "美元"}）：`));
    if (v == null || !v.trim()) return;
    let k = 0;
    cols.forEach((c, j) => {
      const cell = r.cells[j];
      if (!cell || !c.editable || (only.size && !only.has(c.i))) return;
      setCell(c, cell, "px", v.trim()); k++;
    });
    toast(t(`Set on ${k} style(s) — not saved yet`, `已改 ${k} 款——还没保存`));
  }
  function applyBulk() {
    let k = 0;
    sheets.forEach((x, i) => {
      if (!x.mine || (only.size && !only.has(i))) return;
      st.edit(i, (s) => {
        if (n(bulk.fx) > 0) setFx(s, n(bulk.fx));
        if (n(bulk.qty) > 0 && !s.tiers?.qtys.length) s.h.qty = String(Math.round(n(bulk.qty)));
        if (n(bulk.cmMult) > 0) {
          s.qcoef = { ...coefOf(s), cmMult: n(bulk.cmMult) };
          if (s.tiers?.coef) for (const q of Object.keys(s.tiers.coef)) s.tiers.coef[q] = { ...s.tiers.coef[q], cmMult: n(bulk.cmMult) };
        }
        const p = cmPartsOf(s);
        if (isSet(bulk.docs.trim())) {
          const fx = fxOf(s, dfx);
          p.docs = cur === "CNY" ? { ...p.docs, px: fromCny(bulk.docs, fx), cny: bulk.docs.trim(), per: "order" } : { ...p.docs, px: bulk.docs.trim(), cny: undefined, per: "order" };
          if (cur === "CNY" && !s.fx) s.fx = fx;
        }
        setCmParts(s, p);
        if (isSet(bulk.target.trim())) { const mv = moneyFor(cur, fxOf(s, dfx)).typed(bulk.target.trim()); s.target = mv.px; if (mv.cny != null) s.targetCny = mv.cny; else delete s.targetCny; }
      });
      k++;
    });
    toast(t(`Applied to ${k} style(s) — not saved yet`, `已应用到 ${k} 款——还没保存`));
  }
  /** a style's cost sheet: the group's unsaved changes are saved first (JF 2026-10-13: one version of a style at a time) */
  async function openSheet(i: number) {
    try { if (!(await groupSavedOrStay())) return; } catch (e) { toast(e instanceof Error ? e.message : String(e)); return; }
    if (!okToReplace()) return;
    const x = useGroup.getState().sheets[i];
    setSheet(x.data, { id: x.mine ? x.id : null }); markSaved(x.data);
    useStore.getState().setFromGroup({ id: group!.id, name: group!.name }); // the cost sheet shows "← back to the style group"
    go("sheet");
  }
  /** every quantity by default (JF 2026-10-14); "only the quantity shown" when ticked */
  async function exportXlsx(c: "USD" | "CNY") {
    setExporting(true);
    try {
      const { downloadRangeXlsx } = await import("./exportRange.ts");
      const name = await downloadRangeXlsx(group!, sheets.map((x) => x.data), c, dfx, onlyShown ? view : "all", sheets.map((x) => ({ id: x.id, updatedAt: x.updatedAt })), true);
      toast(t("Exported ", "已导出 ") + name);
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setExporting(false); }
  }
  /** the group's Excel back in: first the list of changes, applied only when confirmed (then "Save all") — JF 2026-10-14 */
  async function importXlsx(f: File | undefined) {
    if (!f) return;
    try {
      const { readRangeXlsx } = await import("./importRange.ts");
      const r = await readRangeXlsx(f);
      const known = new Map(sheets.map((x, i) => [x.id, i]));
      const byStyle = new Map<string, ImportStyle>();
      for (const ch of r.changes) {
        const i = known.get(ch.sheetId);
        if (i == null || !sheets[i].mine) continue;
        const exp = r.map.sheets.find((y) => y.id === ch.sheetId);
        const g = byStyle.get(ch.sheetId) || { i, label: exp?.label || styleName(i), conflict: !!exp?.updatedAt && exp.updatedAt !== sheets[i].updatedAt, changes: [] };
        g.changes.push(ch);
        byStyle.set(ch.sheetId, g);
      }
      const list = [...byStyle.values()];
      if (!list.length) { toast(t("No changes found in this Excel", "这个 Excel 里没有修改")); return; }
      setImp({ file: f.name, cur: r.map.cur, styles: list, use: new Set(list.filter((x) => !x.conflict).map((x) => x.i)) });
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); }
  }
  function applyImport() {
    if (!imp) return;
    let k = 0;
    for (const g of imp.styles) {
      if (!imp.use.has(g.i)) continue;
      st.edit(g.i, (s) => {
        const fx = fxOf(s, dfx);
        const money = (v: number | null) => (v == null ? { px: "" } : imp.cur === "CNY" ? { px: fromCny(v, fx), cny: String(v) } : { px: String(v) });
        for (const ch of g.changes) {
          if (ch.field === "target") { const mv = money(ch.to); s.target = mv.px; if (mv.cny != null) s.targetCny = mv.cny; else delete s.targetCny; k++; continue; }
          const colSheet = s.tiers?.qtys.length ? tierSheet(s, ch.qty).sheet : s;
          const cell = rangeRows([colSheet]).find((r) => r.key === ch.rowKey)?.cells[0];
          if (!cell) continue;
          if (imp.cur === "CNY" && ch.field === "px" && !s.fx) s.fx = fx;
          setTierCell(s, ch.qty, cell.ref, ch.field, ch.field === "px" ? money(ch.to) : ch.to == null ? "" : String(ch.to));
          k++;
        }
      });
    }
    setImp(null);
    toast(t(`${k} change(s) filled in — check them, then "Save all"`, `已填入 ${k} 处修改——检查后点「全部保存」`));
  }
  const sections = RANGE_SECTIONS.map(([k, en, zh]) => ({ k, en, zh, rows: rows.filter((r) => r.section === k) })).filter((x) => x.rows.length);
  const span = cols.length + 1;
  const sym = cur === "CNY" ? "¥" : "$";

  return (
    <div className="grp" id="groupPage">
      <div className="grptop">
        <div className="grptitle">
          <span className="lnk" id="grpBack" onClick={back}>← {t("All groups", "全部组合")}</span>
          {mine ? <Field className="in grpname" dataK="grp-name" value={group.name} onChange={(v) => { st.setInfo({ name: v }).catch(() => undefined); }} placeholder={t("Group name", "组合名称")} />
            : <b className="grpname">{group.name}</b>}
          <span className="dim">{sheets.length} {t("styles", "款")}{group.customer ? " · " + group.customer : ""}{!mine && group.displayName ? " · " + group.displayName : ""}</span>
        </div>
        <div className="grpacts">
          {qtys.length > 1 && (
            <span className="grpqtys" id="grpQtys"><span className="dim">{t("Quantity", "显示数量档")}</span>
              <span className="seg">
                <button className={view === "active" ? "on" : ""} data-grp-view="active" onClick={() => setView("active")} title={t("each style at the quantity its cost sheet shows", "每款按它成本表当前那一档")}>{t("as on each sheet", "各款当前档")}</button>
                {qtys.map((q) => <button key={q} className={view === q ? "on" : ""} data-grp-view={q} onClick={() => setView(q)}>{q.toLocaleString()} {t("pcs", "件")}</button>)}
                {anyTiers && <button className={view === "all" ? "on" : ""} data-grp-view="all" onClick={() => setView("all")}>{t("all", "全部")}</button>}
              </span></span>)}
          {/* the order of the styles = the order in the exported Excel (JF 2026-10-16) */}
          {mine && sheets.length > 1 && <button className="btn" id="grpSortNo" onClick={sortByNo} title={t("Put the styles in style-number order (also in the Excel)", "按款号从小到大排（导出的 Excel 也按这个顺序）")}>{t("Sort by style no.", "按款号排序")}</button>}
          {mine && <button className={"btn" + (bulkOpen ? " sheetbtn" : "")} id="grpBulkBtn" onClick={() => setBulkOpen(!bulkOpen)}>{t("Set several styles…", "批量设置…")}</button>}
          {dirty > 0 && <span className="unsaved" id="grpUnsaved">● {t(`${dirty} unsaved`, `${dirty} 款未保存`)}</span>}
          {mine && <button className="btn p" id="grpSave" disabled={saving || !dirty} onClick={save}>{t("Save all", "全部保存")}</button>}
          <button className="btn" id="grpExp" disabled={exporting} onClick={() => exportXlsx("CNY")}>{exporting ? t("Exporting…", "正在导出…") : t("Export Excel (RMB)", "导出合并 Excel（人民币）")}</button>
          <button className="btn sm" id="grpExpUsd" disabled={exporting} onClick={() => exportXlsx("USD")}>{t("in USD", "美元版")}</button>
          <label className="grptog" title={t("Default: every quantity", "默认导出所有数量档")}><input type="checkbox" id="grpOnlyShown" checked={onlyShown} onChange={(e) => setOnlyShown(e.target.checked)} /> {t("only the quantity shown", "只导出当前显示的数量档")}</label>
          {mine && <button className="btn" id="grpImp" onClick={() => impInput.current?.click()} title={t("Read back the pale-yellow cells changed in the exported Excel", "读回导出的 Excel 里改过的淡黄色格子")}>{t("Import Excel", "导入 Excel")}</button>}
          <input type="file" ref={impInput} accept=".xlsx" style={{ display: "none" }} id="grpImpFile" onChange={(e) => { importXlsx(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
      {!mine && <div className="warn">{t("Someone else's group — read only. Open a style to work on a copy.", "这是别人的组合——只能看。打开某一款可以在副本上改。")}</div>}
      {imp && (
        <Modal onClose={() => setImp(null)} wide>
          <h3>{t("Changes in the Excel", "Excel 里的修改")} <span className="dim" style={{ fontSize: 13, fontWeight: 400 }}>· {imp.file}</span></h3>
          <p className="dim" style={{ marginTop: 0, fontSize: 12.5 }}>{t("Tick the styles to take over. They are filled in as unsaved changes — check them, then \"Save all\".", "勾选要采用的款。会作为「未保存」的修改填进组合——检查后再点「全部保存」。")}</p>
          <div id="impList" style={{ maxHeight: 420, overflow: "auto" }}>{imp.styles.map((g) => (
            <div key={g.i} className="impstyle" data-imp-style={g.i}>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 700 }}>
                <input type="checkbox" data-imp-use={g.i} checked={imp.use.has(g.i)} onChange={() => setImp((p) => { if (!p) return p; const u = new Set(p.use); if (u.has(g.i)) u.delete(g.i); else u.add(g.i); return { ...p, use: u }; })} />
                {g.label} <span className="dim" style={{ fontWeight: 400 }}>· {g.changes.length} {t("change(s)", "处修改")}</span></label>
              {g.conflict && <div className="warn" style={{ margin: "4px 0" }} data-imp-conflict={g.i}>{t("Changed in the system after this Excel was exported — not ticked: tick it to use the Excel's numbers anyway.", "这款在导出之后系统里又被修改过——默认不勾选（保留系统的）；要用 Excel 的数字就勾上。")}</div>}
              <table className="t" style={{ marginTop: 4 }}><tbody>{g.changes.map((ch, k) => (
                <tr key={k}><td>{ch.rowLabel}</td><td className="dim">{ch.field === "target" ? "" : `${ch.qty} ${t("pcs", "件")}`}</td>
                  <td>{ch.field === "yld" ? t("yield", "用量") : ch.field === "target" ? t("target", "目标价") : t("unit price", "单价")}</td>
                  <td className="num">{ch.field === "target" ? <>{ch.from ?? "—"} → {ch.to ?? "—"}</> : <>{ch.from ?? "—"} → <b>{ch.to ?? "—"}</b>{ch.field !== "yld" ? (imp.cur === "CNY" ? " ¥" : " $") : ""}</>}</td></tr>))}</tbody></table>
            </div>))}</div>
          <div className="mb"><button className="btn" onClick={() => setImp(null)}>{t("Cancel", "取消")}</button>
            <button className="btn p" id="impApply" disabled={!imp.use.size} onClick={applyImport}>{t(`Fill in ${imp.use.size} style(s)`, `填入 ${imp.use.size} 款`)}</button></div>
        </Modal>
      )}
      {st.missing.length > 0 && <div className="warn">{t(`${st.missing.length} style(s) in this group were deleted.`, `这个组合里有 ${st.missing.length} 款已被删除。`)}</div>}
      {mine && bulkOpen && (
        <div className="grpbulk" id="grpBulk">
          <div className="grpfor"><b>{t("Apply to", "应用到")}</b>
            <button className={"chip2" + (!only.size ? " on" : "")} data-grp-only="all" onClick={() => setOnly(new Set())}>{t("all styles", "全部款")}</button>
            {sheets.map((x, i) => x.mine && (
              <button key={x.id} className={"chip2" + (only.has(i) ? " on" : "")} data-grp-only={i}
                onClick={() => setOnly((p) => { const q = new Set(p); if (q.has(i)) q.delete(i); else q.add(i); return q; })}>{styleName(i)}</button>
            ))}</div>
          <div className="grpfields">
            <label>{t("Order qty", "件数")}<input className="in n" data-bulk="qty" value={bulk.qty} onChange={(e) => setBulk({ ...bulk, qty: e.target.value })} /></label>
            <label>{t("Rate (RMB per USD)", "汇率（1 美元 = ? 人民币）")}<input className="in n" data-bulk="fx" value={bulk.fx} onChange={(e) => setBulk({ ...bulk, fx: e.target.value })} placeholder={String(dfx)} /></label>
            <label>{t("CM factor ×", "CM 倍数 ×")}<input className="in n" data-bulk="cmMult" value={bulk.cmMult} onChange={(e) => setBulk({ ...bulk, cmMult: e.target.value })} /></label>
            <label>{t(`Customs & docs ${sym} / order`, `报关 / 文件费 ${sym} / 整单`)}<input className="in n" data-bulk="docs" value={bulk.docs} onChange={(e) => setBulk({ ...bulk, docs: e.target.value })} /></label>
            <label>{t(`Target price ${sym} / pc`, `目标价 ${sym} / 件`)}<input className="in n" data-bulk="target" value={bulk.target} onChange={(e) => setBulk({ ...bulk, target: e.target.value })} /></label>
            <button className="btn p" id="bulkApply" onClick={applyBulk}>{t("Apply", "应用")}</button>
          </div>
          <div className="dim" style={{ fontSize: 12 }}>{t("Empty fields are left as they are. On CM / other-cost rows, \"=\" sets that row to one amount for these styles.", "空着的不改。CM / 其他费用行左边的「=」可以把这一行统一成同一个金额（同样按上面选的款）。")}</div>
        </div>
      )}
      <div className="grpwrap"><table className="grpt" id="grpTbl">
        <thead>
          <tr className="grphead"><th className="grplab">{t("Style", "款式")}</th>{cols.map((c, j) => {
            if (!c.first) return null;
            const x = sheets[c.i];
            return (
              <th key={j} colSpan={c.span} className="grpcol" data-grp-col={c.i}>
                <div className="grpcard">
                  {x.data.sketch ? <img src={x.data.sketch} alt="" /> : <span className="nosk">{t("no sketch", "无图")}</span>}
                  <div className="grpcardt">
                    <b title={x.data.h.style}>{x.data.h.style || x.styleNo || "—"}</b>
                    <span title={x.data.h.name}>{x.data.h.name || x.title}</span>
                    <span className="lnk" data-grp-open={c.i} onClick={() => openSheet(c.i)}>{t("open", "打开成本表")} →</span>
                    {mine && sheets.length > 1 && <span className="grpmove">
                      <button className="ico" data-grp-left={c.i} disabled={c.i === 0} title={t("move left", "往左移")} onClick={() => move(c.i, -1)}>←</button>
                      <button className="ico" data-grp-right={c.i} disabled={c.i === sheets.length - 1} title={t("move right", "往右移")} onClick={() => move(c.i, 1)}>→</button></span>}
                  </div>
                </div>
              </th>);
          })}</tr>
          <tr className="grpqty"><th className="grplab">{t("Order qty", "件数")}</th>{cols.map((c, j) => (
            <th key={j} className={c.missing ? "grpmiss" : c.span > 1 && c.editable ? "tieron" : ""}>{c.missing ? t(`no ${c.qty} pcs quote`, `无 ${c.qty} 件报价`) : <>{c.qty ? c.qty.toLocaleString() : "—"}{c.span > 1 && c.editable ? t(" · shown", "（当前档）") : ""}</>}</th>))}</tr>
        </thead>
        <tbody>
          {sections.map((sec) => (
            <Fragment key={sec.k}>
              <tr className={"grpsec sec-" + sec.k}><td className="grplab">{t(sec.en, sec.zh)}</td><td colSpan={span - 1}></td></tr>
              {sec.rows.map((r) => (
                <tr key={r.key} data-grp-row={r.key}>
                  <td className="grplab">{mine && rowIsItem(r.key) && <button className="grpall" title={t("Same amount on every style", "这一行统一为同一个金额")} data-grp-all={r.key} onClick={() => rowToAll(r)}>=</button>}
                    <span>{lang === "zh" ? r.zh : r.en}</span></td>
                  {cols.map((c, j) => {
                    const cell = r.cells[j];
                    if (!cell) return <td key={j} className="grpna">{c.missing ? "" : "—"}</td>;
                    const m = money(c);
                    return (
                      <td key={j} className="grpcell">
                        <div className="grpdesc" title={[cell.desc, cell.pos].filter(Boolean).join(" · ")}>{cell.desc || " "}{cell.pos ? <span className="dim"> · {cell.pos}</span> : null}</div>
                        <div className="grpnums">
                          {c.editable
                            ? <Field className="n" dataK={`grp-px-${c.i}-${r.key}`} value={m.shown(cell.px, cell.cny)} onChange={(v) => setCell(c, cell, "px", v)} />
                            : <span className="ro">{m.shown(cell.px, cell.cny) || "—"}</span>}
                          {cell.yld != null ? <>
                            <span className="x">×</span>
                            {c.editable ? <Field className="n" dataK={`grp-yld-${c.i}-${r.key}`} value={cell.yld} onChange={(v) => setCell(c, cell, "yld", v)} /> : <span className="ro">{cell.yld}</span>}
                            <span className="u">{unitLabel(cell.unit || "pcs", lang)}</span></>
                            : cell.per ? (c.editable
                              ? <select className="per" data-grp-per={`${c.i}-${r.key}`} value={cell.per} onChange={(e) => setCell(c, cell, "per", e.target.value)}>
                                  <option value="pc">{t("/ pc", "每件")}</option><option value="order">{t("/ order", "整单")}</option></select>
                              : <span className="u">{cell.per === "order" ? t("/ order", "整单") : t("/ pc", "每件")}</span>) : null}
                          <b className="amt" data-grp-amt={`${c.i}-${r.key}`}>{m.num(cell.actual)}</b>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </Fragment>
          ))}
          <tr className="grpsec sec-sum"><td className="grplab">{t(`Per piece (${sym})`, `每件汇总（${sym}）`)}</td><td colSpan={span - 1}></td></tr>
          {sections.map((sec) => (
            <tr key={"t" + sec.k} className="grptot"><td className="grplab">{t(sec.en, sec.zh)}</td>{cols.map((c, j) => <td key={j}>{c.missing ? "—" : money(c).num(tot[j].bySection[sec.k])}</td>)}</tr>
          ))}
          <tr className="grptot strong" data-grp-tot="fob"><td className="grplab">{t("Cost price (actual FOB)", "成本价")}</td>{cols.map((c, j) => <td key={j}>{c.missing ? "—" : money(c).fmt(tot[j].totals.fob)}</td>)}</tr>
          <tr className="grptot strong" data-grp-tot="qfob"><td className="grplab">{t("Quoted FOB", "报价")}</td>{cols.map((c, j) => <td key={j}>{c.missing ? "—" : money(c).fmt(tot[j].totals.qfob)}</td>)}</tr>
          <tr className="grptot" data-grp-tot="gpp"><td className="grplab">GP %</td>{cols.map((c, j) => <td key={j} className={tot[j].totals.gp < 0 ? "neg" : ""}>{c.missing ? "—" : (tot[j].totals.gpp * 100).toFixed(1) + "%"}</td>)}</tr>
          <tr className="grptot" data-grp-tot="gpo"><td className="grplab">{t("GP total (order)", "整单毛利")}</td>{cols.map((c, j) => <td key={j}>{c.qty && !c.missing ? money(c).fmt(tot[j].totals.gp * c.qty) : "—"}</td>)}</tr>
          <tr className="grptot" data-grp-tot="target"><td className="grplab">{t("Customer target", "客户目标价")}</td>{cols.map((c, j) => {
            const s = c.sheet;
            return <td key={j}>{c.editable
              ? <Field className="n" dataK={`grp-target-${c.i}`} value={money(c).shown(s.target || "", s.targetCny)} onChange={(v) => st.edit(c.i, (x) => { const mv = money(c).typed(v); x.target = mv.px; if (mv.cny != null) x.targetCny = mv.cny; else delete x.targetCny; })} placeholder="—" />
              : s.target ? money(c).fmt(n(s.target)) : "—"}</td>;
          })}</tr>
          <tr className="grptot" data-grp-tot="vs"><td className="grplab">{t("Quote − target", "报价 − 目标价")}</td>{cols.map((c, j) => {
            const d = tot[j].target && !c.missing ? tot[j].totals.qfob - tot[j].target : null;
            return <td key={j} className={d != null && d > 0 ? "neg" : ""}>{d == null ? "—" : (d > 0 ? "+" : "") + money(c).fmt(d)}</td>;
          })}</tr>
        </tbody>
      </table></div>
    </div>
  );
}

/** Groups list + "new group" from saved cost sheets. */
function GroupHome() {
  const t = useT();
  const user = useStore((s) => s.user);
  const { toast } = useStore.getState();
  const open = useGroup((s) => s.open);
  const [all, setAll] = useState(false);
  const [groups, setGroups] = useState<GroupInfo[] | null>(null);
  const [q, setQ] = useState("");
  const [sheets, setSheets] = useState<SheetSummary[]>([]);
  const [pick, setPick] = useState<string[]>([]);
  const [name, setName] = useState("");
  const load = () => api.groups(all).then((r) => setGroups(r.items)).catch((e) => toast(String(e.message || e)));
  useEffect(() => { load(); }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const tm = setTimeout(() => api.sheets(q).then((r) => setSheets(r.items)).catch(() => undefined), 200); return () => clearTimeout(tm); }, [q]);
  async function create() {
    if (!pick.length) { toast(t("Tick the styles first", "请先勾选款式")); return; }
    const first = sheets.find((x) => x.id === pick[0]);
    const customer = first?.customer || "";
    const r = await api.createGroup({ name: name.trim() || `${customer || t("Styles", "款式")} ${new Date().toISOString().slice(0, 10)}`, customer, sheetIds: pick });
    setPick([]); setName("");
    await open(r.id);
  }
  async function del(g: GroupInfo) {
    if (!confirm(t(`Delete the group "${g.name}"? The cost sheets stay.`, `删除组合「${g.name}」？里面的成本表不会被删除。`))) return;
    await api.deleteGroup(g.id); load();
  }
  return (
    <div className="grphome">
      <div className="panel">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}><h3 style={{ margin: 0 }}>{t("Style groups", "款式组合")}</h3>
          {user?.role === "admin" && <label className="dim" style={{ marginLeft: "auto", fontSize: 12.5 }}><input type="checkbox" id="grpAll" checked={all} onChange={(e) => setAll(e.target.checked)} /> {t("everyone's (admin)", "看所有人的（管理员）")}</label>}</div>
        <p className="dim" style={{ fontSize: 12.5 }}>{t("Several styles of one customer side by side — edit them together and export one Excel for the first approval.", "同一客户的多款横向放在一起——一起改，导出一份 Excel 给客户做初审。")}</p>
        <table className="t" id="grpList"><tbody>
          <tr><th>{t("Group", "组合")}</th><th>{t("Customer", "客户")}</th><th>{t("Styles", "款数")}</th>{all && <th>{t("Owner", "所有人")}</th>}<th>{t("Updated", "更新")}</th><th></th></tr>
          {groups?.map((g) => (
            <tr key={g.id} className="rec" data-grp={g.id}><td onClick={() => open(g.id)}><b>{g.name || "—"}</b></td><td onClick={() => open(g.id)}>{g.customer}</td><td onClick={() => open(g.id)}>{g.sheetIds.length}</td>
              {all && <td>{g.displayName || g.username || "—"}</td>}<td className="dim">{localTime(g.updatedAt)}</td>
              <td>{g.owner === user?.id && <span className="lnk dim" data-grp-del={g.id} onClick={() => del(g)}>{t("Delete", "删除")}</span>}</td></tr>
          ))}
        </tbody></table>
        {groups && !groups.length && <div className="dim">{t("No groups yet.", "还没有组合。")}</div>}
      </div>
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>{t("New group from saved cost sheets", "用保存的成本表新建组合")}</h3>
        <div className="fl"><input className="in" id="grpSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search customer / style no. / name", "搜索客户 / 款号 / 款名")} /></div>
        <div className="grppick"><table className="t"><tbody>
          {sheets.map((x) => (
            <tr key={x.id} className="rec" onClick={() => setPick((p) => (p.includes(x.id) ? p.filter((y) => y !== x.id) : [...p, x.id]))}>
              <td><input type="checkbox" data-grp-pick={x.id} readOnly checked={pick.includes(x.id)} /></td><td className="mono">{x.styleNo || "—"}</td><td>{x.customer}</td><td>{x.title}</td></tr>
          ))}
        </tbody></table></div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input className="in" id="grpName" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Group name (default: customer + date)", "组合名称（不填 = 客户 + 日期）")} />
          <button className="btn p" id="grpCreate" onClick={() => create().catch((e) => toast(String(e.message || e)))}>{t(`Group ${pick.length} style(s)`, `把 ${pick.length} 款放进组合`)}</button>
        </div>
      </div>
    </div>
  );
}
